import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useToast } from '../contexts/ToastContext';
import myTicketsService from '../services/myTickets.service';
import { useVisibilityAwareInterval } from '../hooks/useVisibilityAwareInterval';

const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Polls for the current user's bug/feature submissions that have just been
 * solved or closed in Zendesk and fires a purple toast for each. Acknowledges
 * each ticket immediately after queuing its toast so subsequent polls (or
 * a page reload) don't re-show it — the toast itself is persistent until
 * the user clicks through or hits X.
 */
const ClosedTicketToast: React.FC = () => {
  const { accounts } = useMsal();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const inFlight = useRef(false);
  const isSignedIn = accounts.length > 0;

  const check = async () => {
    if (!isSignedIn) return;
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      const tickets = await myTicketsService.getUnseenClosed();
      for (const ticket of tickets) {
        const isBug = ticket.submission_type === 'bug';
        showToast({
          type: isBug ? 'bug-closed' : 'feature-closed',
          message: `${isBug ? 'Bug' : ticket.submission_type === 'helpdesk' ? 'Helpdesk ticket' : 'Feature'} #${ticket.id} resolved: ${ticket.subject}`,
          duration: 0,
          onClick: () => navigate('/my-tickets'),
        });
        // Ack immediately so repeat polls don't re-notify, regardless of
        // whether the user clicks the toast body or dismisses with X.
        myTicketsService.acknowledgeTicket(ticket.id).catch((err) => {
          console.error('Failed to acknowledge closed ticket:', err);
        });
      }
    } catch (err) {
      // Silently fail — don't interrupt the user for a polling error.
      console.error('Failed to check for closed tickets:', err);
    } finally {
      inFlight.current = false;
    }
  };

  useEffect(() => {
    if (isSignedIn) check();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSignedIn, location.pathname]);

  useVisibilityAwareInterval(check, isSignedIn ? POLL_INTERVAL_MS : null);

  return null;
};

export default ClosedTicketToast;
